/**
 * IndexedDB-backed store for pending share-target drafts.
 *
 * Web equivalent of `kaether_for_ios/Stoat/E2EE/SharePendingDraftStore.swift`
 * (which writes JSON files to an App-Group container under
 * `.completeFileProtection`). On the web, the SW handler and main thread
 * share an IndexedDB origin, so a single IDB database is the natural
 * substitute. There is no equivalent of `.completeFileProtection` on the
 * web — this is a documented platform gap in `Signal_standard_web.md`.
 *
 * THREAT MODEL
 *   The store sits between an UNTRUSTED system boundary (the SW `fetch`
 *   handler that just parsed an OS-supplied multipart body) and the
 *   trusted in-app composer. The store's invariants:
 *
 *   - Drafts are NEVER auto-sent. The SW writes the draft and redirects
 *     the user to a composer URL; the composer is responsible for the
 *     explicit user-confirm step.
 *   - Drafts EXPIRE. Each draft carries `expiresAt`; the store skips
 *     expired records on read (so they appear absent) and a `purgeExpired`
 *     primitive deletes them physically. Default TTL: 1 hour.
 *   - There is no read-by-channel index. The draft is keyed by ULID and
 *     pulled by the composer using the id it received in the redirect
 *     URL's query string. A leaked id is the only way to read another
 *     draft, and id leak requires same-origin access (where everything
 *     else is also readable).
 *
 * Service-Worker importability guarantee:
 *   This file uses NO `window`, `document`, or DOM-only APIs. IndexedDB only.
 */

import type { SharePendingDraft } from "./ShareTargetPayload";

const DB_VERSION = 1;
const STORE_NAME = "drafts";

/** Default draft TTL — 1 hour from `createdAt`. */
export const DEFAULT_DRAFT_TTL_MS = 60 * 60 * 1000;

/**
 * Shape persisted in IDB. Identical to `SharePendingDraft` but with
 * `plaintextBytes` (Uint8Array) preserved through the structured-clone path
 * IDB provides natively — no manual base64 round-trip needed.
 */
type PersistedDraft = SharePendingDraft;

function openDb(dbName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, DB_VERSION);
    req.onupgradeneeded = () => {
      // Single object store keyed by the draft id (ULID). No indices —
      // drafts are looked up exclusively by id from the composer.
      if (!req.result.objectStoreNames.contains(STORE_NAME)) {
        req.result.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function txWrite(db: IDBDatabase, id: string, draft: PersistedDraft): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(draft, id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function txRead(db: IDBDatabase, id: string): Promise<PersistedDraft | null> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(id);
    req.onsuccess = () => resolve((req.result as PersistedDraft | undefined) ?? null);
    req.onerror = () => reject(req.error);
  });
}

function txDelete(db: IDBDatabase, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function txPurgeExpired(db: IDBDatabase, now: number): Promise<number> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const cursorReq = store.openCursor();
    let purged = 0;
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (!cursor) return; // tx.oncomplete fires next
      const draft = cursor.value as PersistedDraft;
      if (draft.expiresAt <= now) {
        cursor.delete();
        purged += 1;
      }
      cursor.continue();
    };
    cursorReq.onerror = () => reject(cursorReq.error);
    tx.oncomplete = () => resolve(purged);
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Persistent draft-pending store for share-target handoffs.
 *
 * Usage — instantiate per-context (main thread / Service Worker) with the
 * same `dbName` so both contexts share state:
 *
 * ```ts
 * const store = new ShareTargetStore();
 * await store.put(draft);
 * const draft = await store.get(id);
 * await store.delete(id);
 * ```
 */
export class ShareTargetStore {
  /** Shared singleton for the default DB name. Lazy-initialised. */
  private static _shared: ShareTargetStore | null = null;

  static get shared(): ShareTargetStore {
    if (!ShareTargetStore._shared) {
      ShareTargetStore._shared = new ShareTargetStore();
    }
    return ShareTargetStore._shared;
  }

  private readonly dbName: string;
  private _db: IDBDatabase | null = null;

  constructor(dbName = "kaether:share-target:v1") {
    this.dbName = dbName;
  }

  private async db(): Promise<IDBDatabase> {
    if (!this._db) {
      this._db = await openDb(this.dbName);
    }
    return this._db;
  }

  /**
   * Persist a draft. Caller is responsible for setting `expiresAt`
   * sensibly; use `DEFAULT_DRAFT_TTL_MS` if unsure.
   */
  async put(draft: SharePendingDraft): Promise<void> {
    const db = await this.db();
    await txWrite(db, draft.id, draft);
  }

  /**
   * Read a draft by id. Returns `null` if the draft does not exist OR if
   * it has expired (the caller cannot distinguish; this is intentional —
   * an expired draft is logically absent). Expired drafts are deleted on
   * read so the IDB does not bloat.
   */
  async get(id: string, now: number = Date.now()): Promise<SharePendingDraft | null> {
    const db = await this.db();
    const draft = await txRead(db, id);
    if (!draft) return null;
    if (draft.expiresAt <= now) {
      // Auto-purge expired draft on read.
      await txDelete(db, id);
      return null;
    }
    return draft;
  }

  /** Explicit delete — called by the composer after the user confirms the send. */
  async delete(id: string): Promise<void> {
    const db = await this.db();
    await txDelete(db, id);
  }

  /**
   * Sweep expired drafts. Intended for periodic SW maintenance (e.g. on
   * `activate` or during idle). Returns the count of drafts purged.
   */
  async purgeExpired(now: number = Date.now()): Promise<number> {
    const db = await this.db();
    return txPurgeExpired(db, now);
  }
}

// ---------------------------------------------------------------------------
// ULID helper — used by the SW handler to mint the draft id.
// ---------------------------------------------------------------------------

const ULID_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ"; // Crockford's base32

/**
 * Generates a 26-char ULID. Time component is 10 chars (48 bits of unix
 * ms), random component is 16 chars (80 bits). Crypto-strong random.
 *
 * The SW context has `crypto.getRandomValues`; we do not import a heavy
 * ULID library to keep the SW bundle small.
 */
export function newDraftId(now: number = Date.now()): string {
  // 10-char time component
  let time = "";
  let t = now;
  for (let i = 9; i >= 0; i--) {
    const mod = t % 32;
    time = ULID_ALPHABET[mod] + time;
    t = Math.floor(t / 32);
  }

  // 16-char random component
  const rand = new Uint8Array(16);
  crypto.getRandomValues(rand);
  let random = "";
  for (let i = 0; i < 16; i++) {
    random += ULID_ALPHABET[rand[i] % 32];
  }

  return time + random;
}
